import {
  BaseQueryApi,
  BaseQueryFn,
  BaseQueryMeta,
  BaseQueryResult,
  createApi,
  EndpointBuilder,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
  retry,
  TagDescription,
} from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '@/api/common';
import { configuration } from '@/config';
import { mockPendingAnnouncements } from '@/data/mockAnnouncements';
import {
  createMockCustomerKYXVerificationsPage,
  getMockCustomerKYXVerification,
} from '@/data/mockCustomerKYXVerifications';
import { mockIntegratorRevenueSummary } from '@/data/mockIntegratorEarnings';
import { mockIntegratorTotalEarnings } from '@/data/mockIntegratorTotalEarnings';
import {
  createMockTransactionDetails,
  getMockTransactionStatusLogs,
} from '@/data/mockTransactionDetails';
import { createMockTransactionsPage } from '@/data/mockTransactions';
import {
  CreateDashboardKYXVerificationRequest,
  DashboardKYXVerificationResponse,
} from '@/DTOs/dashboardKYXVerifications';
import { DownloadTransactionDocumentRequest } from '@/DTOs/downloadTransactionDocumentRequest';
import {
  DeleteFileRequest,
  DownloadFileRequest,
  FileMetadata,
  UploadFileRequest,
} from '@/DTOs/files';
import { CreateGroupRequest, Group, UpdateGroupRequest } from '@/DTOs/groups';
import {
  GetOwnIntegratorFeaturesResponse,
  IntegratorEarningsWithdrawalRequest,
  IntegratorRevenueSummaryParams,
  IntegratorRevenueSummaryResponse,
  IntegratorTotalEarningsResponse,
} from '@/DTOs/integrators';
import { PaymentInstructionsRequest, PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { CreateTotpRequest, CreateTotpResponse } from '@/DTOs/totp';
import { UpdateDepositTransactionHashRequest } from '@/DTOs/updateDepositTransactionHash';
import { UpdateUserPreferencesRequest, UserPreferencesResponse } from '@/DTOs/userPreferences';
import { CreateUserRequest, UpdateUserRequest } from '@/DTOs/users';
import { CreateBeneficiaryVirtualAccountRequest, VirtualAccount } from '@/DTOs/virtualAccount';
import { WalletRequest, WalletResponse } from '@/DTOs/walletResponse';
import { mockPaymentInstructions } from '@/mocks/mockPaymentInstruction';
import {
  AddressAutocompleteRequest,
  AddressAutocompleteResponse,
  Place,
} from '@/types/addressValidation';
import { PendingAnnouncementsResponse } from '@/types/announcement';
import {
  BankInfoRequest,
  BankInfoResponse,
  DetailedBalance,
  DetailedBalancesRequest,
  DetailedMultiCurrenciesBalancesRequest,
  IbanBankInfoResponse,
  SwiftBankInfoResponse,
} from '@/types/bankInfo';
import {
  BeneficiariesPageResponse,
  Beneficiary,
  BeneficiaryApiResponse,
} from '@/types/beneficiary';
import { BitsoBank } from '@/types/bitsoBank';
import { isArrayOf } from '@/types/common-type-guards';
import { ConfigurationResponse } from '@/types/configuration';
import {
  Contact,
  CreateContactRequest,
  CreateFiatAccountRequest,
  CreateFiatAccountResponse,
  FiatAccount,
  UpdateContactRequest,
  UpdateFiatAccountRequest,
} from '@/types/contacts';
import { CurrencyCodeType, DashboardOperation, PaymentRailType } from '@/types/currency';
import { BeneficiaryType, Customer } from '@/types/customer';
import { CustomerKYXVerification } from '@/types/customerKYXVerification';
import { GetCustomerKYXVerificationsFilters } from '@/types/getCustomerKYXVerificationsFilters';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { isNamedIdentified, NamedIdentified } from '@/types/namedIdentified';
import { Nullable } from '@/types/nullable';
import { Page } from '@/types/page';
import { ProofOfPaymentType } from '@/types/proofOfPaymentType';
import {
  CreateRecipientRequest,
  RecipientResponse,
  UpdateRecipientNicknameRequest,
} from '@/types/recipient';
import { SimulationRequest, SimulationResponse } from '@/types/simulation';
import { SpeiBank } from '@/types/speiBank';
import {
  MockDepositRequest,
  PoboPayoutRequest,
  PoboPayoutResponse,
  SimplifiedTransactionStatus,
  Transaction,
  TransactionDocument,
  TransactionNotification,
  TransactionRequest,
  TransactionResponse,
  TransactionType,
  TransactionWithDocuments,
  UploadEfxTransactionACAM220FileRequest,
} from '@/types/transaction';
import { TransactionStatusLogResponse } from '@/types/transactionStatusLog';
import { GetUsersFilters, User } from '@/types/user';
import { extractFileNameFromContentDispositionHeader } from '@/utility/extractFileNameFromContentDispositionHeader';
import {
  resolveFromAccount,
  resolveToAccount,
  resolveToRecipient,
} from '@/utility/transactionAccountResolvers';
import {
  customerFileRemovePatcher,
  customerFileUploadPatcher,
  isCustomerFilePath,
} from '@/utility/v1helpers';

const reducerPath = 'caliza.v1' as const;

const shouldMock = (): boolean => {
  return configuration.mock;
};

type TagTypes =
  | 'UserProfile'
  | 'UserPreferences'
  | 'Currencies'
  | 'Simulation'
  | 'Contacts'
  | 'Balances'
  | 'Transactions'
  | 'FiatAccounts'
  | 'Recipients'
  | 'Users'
  | 'Groups'
  | 'Configuration'
  | 'CustomerKYXVerifications'
  | 'Beneficiaries'
  | 'OwnIntegratorFeatures'
  | 'IntegratorTotalEarnings'
  | 'IntegratorRevenueSummary'
  | 'BeneficiaryVirtualAccounts'
  | 'Announcements';

const getInvalidateBalancesTag = (
  result: TransactionResponse | undefined,
): readonly TagDescription<TagTypes>[] => {
  if (result && result.from) {
    const { currencyCode } = result.from;
    return [{ type: 'Balances', id: currencyCode }, 'Transactions'];
  }

  return ['Balances', 'Transactions'];
};

const getBalancesTagDescription = (
  result: DetailedBalance | undefined,
): readonly TagDescription<TagTypes>[] =>
  result ? [{ type: 'Balances', id: result.currencyCode }] : ['Balances'];

type BaseQuery = ReturnType<typeof fetchBaseQuery>;
type ReducerPath = 'caliza-api';

const paramsSerializer = (params: Record<string, any>): string => {
  const entries = Object.entries(params);
  return entries
    .filter(([, value]: [string, any]): boolean => {
      if (value === undefined || value === null) {
        return false;
      } else if (Array.isArray(value)) {
        return value.length > 0;
      }

      return true;
    })
    .reduce(
      (
        reduced: ReadonlyArray<[string, any]>,
        [key, value]: [string, any],
      ): ReadonlyArray<[string, any]> => {
        if (Array.isArray(value)) {
          return [...reduced, ...value.map((value: any): [string, any] => [key, value])];
        }
        return [...reduced, [key, value]];
      },
      [],
    )
    .map(([key, value]: [string, any]): string => `${key}=${value}`)
    .join('&');
};

const query = fetchBaseQuery({
  baseUrl: configuration.calizaAPIBaseURL,
  paramsSerializer: paramsSerializer,
  prepareHeaders: prepareHeaders,
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  any,
  FetchBaseQueryMeta
> = async (
  args: string | FetchArgs,
  api: any,
  extraOptions: any,
): Promise<BaseQueryResult<any>> => {
  if (typeof args === 'string' && args === '') {
    return { data: undefined };
  } else if (typeof args === 'object' && args.url === '') {
    return { data: undefined };
  }

  return query(args, api, extraOptions);
};

export const calizaApi = createApi({
  baseQuery: retry(customBaseQuery, {
    /* Retry doesn't make any sense.
     *
     * If the request failed, there's a reason for that.
     */
    maxRetries: 0,
  }),
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  endpoints: (build: EndpointBuilder<BaseQuery, TagTypes, ReducerPath>) => {
    return {
      bankLookup: build.query<BankInfoResponse, BankInfoRequest | undefined>({
        query: (request: BankInfoRequest | undefined): FetchArgs => {
          return {
            url: request
              ? `/v1/bank-info/${request.routingNumber}?paymentType=${request.paymentType}`
              : '',
          };
        },
      }),
      swiftCodeLookup: build.query<SwiftBankInfoResponse, string>({
        query: (swiftCode: string): FetchArgs => {
          return {
            url: `/v1/bank-info/swift/${swiftCode}`,
          };
        },
      }),
      ibanCodeLookup: build.query<IbanBankInfoResponse, string>({
        query: (ibanCode: string): FetchArgs => {
          return {
            url: `/v1/bank-info/iban/${ibanCode}`,
          };
        },
      }),
      getPlaceDetails: build.query<Place, string>({
        query: (placeId: string): FetchArgs => {
          return {
            url: `/v1/addresses/places/${placeId}`,
          };
        },
      }),
      autocompleteAddress: build.query<AddressAutocompleteResponse, AddressAutocompleteRequest>({
        query: (req: AddressAutocompleteRequest): FetchArgs => ({
          url: `/v1/addresses/autocomplete`,
          params: {
            country: req.country,
            address: req.address,
          },
        }),
      }),
      createContact: build.mutation<Contact, CreateContactRequest>({
        query: (body: CreateContactRequest): FetchArgs => ({
          url: '/v1/contacts',
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['Contacts'],
      }),
      createFakeSpeiPayment: build.mutation<void, MockDepositRequest>({
        query: (request: MockDepositRequest): FetchArgs => ({
          url: '/v1/transactions/sandbox/spei',
          method: 'POST',
          body: request,
        }),
      }),
      createFakePIXPayment: build.mutation<void, MockDepositRequest>({
        query: (request: MockDepositRequest): FetchArgs => ({
          url: '/v1/transactions/sandbox/pix',
          method: 'POST',
          body: request,
        }),
      }),
      createFiatAccount: build.mutation<CreateFiatAccountResponse, CreateFiatAccountRequest>({
        query: (body: CreateFiatAccountRequest): FetchArgs => ({
          url: '/v1/fiat-accounts',
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['FiatAccounts', 'Contacts'],
      }),
      createIntegrator: build.mutation<any, void>({
        query: (): FetchArgs => ({
          url: '/v1/customer-signup/integrator',
          method: 'POST',
        }),
      }),
      getOwnIntegratorFeatures: build.query<GetOwnIntegratorFeaturesResponse, void>({
        query: (): FetchArgs => ({
          url: '/v1/integrators/me/features',
        }),
        providesTags: ['OwnIntegratorFeatures'],
      }),
      getIntegratorTotalEarnings: build.query<IntegratorTotalEarningsResponse, void>({
        query: (): FetchArgs => {
          if (shouldMock()) {
            return { url: '' };
          }
          return { url: '/v1/integrators/me/total-earnings' };
        },
        transformResponse: (
          response: IntegratorTotalEarningsResponse,
        ): IntegratorTotalEarningsResponse => {
          if (shouldMock()) {
            return mockIntegratorTotalEarnings;
          }
          return response;
        },
        providesTags: ['IntegratorTotalEarnings'],
      }),
      getIntegratorRevenueSummary: build.query<
        IntegratorRevenueSummaryResponse,
        IntegratorRevenueSummaryParams
      >({
        query: ({ fromDate, toDate }: IntegratorRevenueSummaryParams): FetchArgs => {
          if (shouldMock()) {
            return { url: '' };
          }
          return {
            url: '/v1/integrators/me/revenue-summary',
            params: { fromDate: fromDate.substring(0, 10), toDate: toDate.substring(0, 10) },
          };
        },
        transformResponse: (
          response: IntegratorRevenueSummaryResponse,
        ): IntegratorRevenueSummaryResponse => {
          if (shouldMock()) {
            return mockIntegratorRevenueSummary;
          }
          return response;
        },
        providesTags: ['IntegratorRevenueSummary'],
      }),
      withdrawIntegratorEarnings: build.mutation<void, IntegratorEarningsWithdrawalRequest>({
        query: (request: IntegratorEarningsWithdrawalRequest): FetchArgs => ({
          url: '/v1/integrators/me/revenue-withdrawal',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['IntegratorTotalEarnings'],
      }),
      createSimulation: build.mutation<SimulationResponse, Nullable<SimulationRequest>>({
        query: (request: Nullable<SimulationRequest>): FetchArgs => ({
          url: '/v1/simulations',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Simulation'],
      }),
      createTransaction: build.mutation<TransactionResponse, TransactionRequest>({
        query: (transactionRequest: TransactionRequest): FetchArgs => {
          const { attachment, ...request } = transactionRequest;
          const formData = new FormData();

          if (attachment) {
            formData.append('files', attachment);
          }
          formData.append('request', JSON.stringify(request));

          return {
            url: '/v1/transactions/:withDocuments',
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: getInvalidateBalancesTag,
      }),
      uploadEfxTransactionACAM220file: build.mutation<
        TransactionDocument,
        UploadEfxTransactionACAM220FileRequest
      >({
        query: ({ id, file }: UploadEfxTransactionACAM220FileRequest): FetchArgs => {
          const formData = new FormData();
          formData.append('file', file);

          return {
            url: `/v1/transactions/${id}/documents/ACAM220`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: ['Transactions'],
      }),
      deleteContact: build.mutation<void, string>({
        query: (id: string): FetchArgs => ({
          url: `/v1/contacts/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Contacts'],
      }),
      deleteFiatAccount: build.mutation<void, string>({
        query: (id: string): FetchArgs => ({
          url: `/v1/fiat-accounts/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['FiatAccounts', 'Contacts'],
      }),
      createRecipient: build.mutation<RecipientResponse, CreateRecipientRequest>({
        query: (body: CreateRecipientRequest): FetchArgs => ({
          url: '/v1/recipients',
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['Recipients', 'Contacts'],
      }),
      deleteRecipient: build.mutation<void, string>({
        query: (id: string): FetchArgs => ({
          url: `/v1/recipients/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Recipients', 'Contacts'],
      }),
      getRecipient: build.query<RecipientResponse, string>({
        query: (id: string): string => `/v1/recipients/${id}`,
        providesTags: (
          result: RecipientResponse | undefined,
        ): readonly TagDescription<TagTypes>[] => [{ type: 'Recipients', id: result?.id }],
      }),
      getRecipients: build.query<
        Page<RecipientResponse>,
        { beneficiaryId: string | null; size?: number }
      >({
        query: ({
          beneficiaryId,
          size,
        }: {
          beneficiaryId: string | null;
          size?: number;
        }): string => {
          if (!beneficiaryId) {
            return '';
          }
          const params = size ? `&size=${size}` : '';
          return `/v1/recipients?beneficiaryId=${beneficiaryId}${params}`;
        },
        providesTags: (
          result: Page<RecipientResponse> | undefined,
        ): readonly TagDescription<TagTypes>[] =>
          result
            ? [
                { type: 'Recipients', id: 'LIST' },
                ...result.content.map(
                  ({ id }: RecipientResponse): TagDescription<TagTypes> =>
                    ({ type: 'Recipients', id }) as const,
                ),
              ]
            : [{ type: 'Recipients', id: 'LIST' }],
      }),
      updateRecipientNickname: build.mutation<
        RecipientResponse,
        { id: string; data: UpdateRecipientNicknameRequest }
      >({
        query: ({ id, data }: { id: string; data: UpdateRecipientNicknameRequest }): FetchArgs => ({
          url: `/v1/recipients/${id}/nickname`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (
          _result: RecipientResponse | undefined,
          _error: FetchBaseQueryError | undefined,
          arg: { id: string; data: UpdateRecipientNicknameRequest },
        ): readonly TagDescription<TagTypes>[] => [{ type: 'Recipients', id: arg.id }, 'Contacts'],
      }),
      deleteFile: build.mutation<unknown, DeleteFileRequest>({
        query: ({ path, id }: DeleteFileRequest): FetchArgs => {
          return {
            url: `/v1/customer-signup/${path}/${id}`,
            method: 'DELETE',
          };
        },
        onQueryStarted: async (
          request: DeleteFileRequest,
          // React toolkit is a stupid toolkit that thinks
          // it knows better. And that type inference is better
          // than explicitly stating what thing is what type.
          //
          // IMHO this makes having types completely pointless.
          //
          // eslint-disable-next-line @typescript-eslint/typedef
          { dispatch, queryFulfilled },
        ): Promise<void> => {
          if (isCustomerFilePath(request.path)) {
            const { util } = calizaApi;

            const patchResult = dispatch(
              util.updateQueryData('getCustomer', undefined, customerFileRemovePatcher(request)),
            );

            try {
              await queryFulfilled;
            } catch (error: unknown) {
              console.debug(error);
              // TODO: Dispatch an action to indicate there was an error and perhaps
              //       give feedback to the user.
              patchResult.undo();
            }
          }
        },
      }),
      downloadFile: build.query<string, DownloadFileRequest>({
        query: ({ path, id }: DownloadFileRequest): FetchArgs => ({
          url: `/v1/customer-signup/${path}/${id}`,
          method: 'GET',
          responseHandler: (response: Response): Promise<string> => response.text(),
        }),
      }),
      downloadFileInfo: build.query<FileMetadata, DownloadFileRequest>({
        query: ({ path, id }: DownloadFileRequest): FetchArgs => ({
          url: `/v1/customer-signup/${path}/${id}`,
          method: 'HEAD',
        }),
        transformResponse: async (
          _: BaseQueryResult<BaseQuery>,
          meta: BaseQueryMeta<BaseQuery>,
        ): Promise<FileMetadata> => {
          if (!meta) {
            throw new Error('No meta in redux toolkit fetch response');
          }

          const { response } = meta;
          if (!response) {
            throw new Error('No response in redux toolkit fetch response');
          }

          const { headers } = response;

          return {
            size: Number(headers.get('content-length')),
            mimeType: headers.get('content-type'),
            name: extractFileNameFromContentDispositionHeader(headers.get('content-disposition')),
          };
        },
      }),
      getBeneficiaryWallet: build.query<WalletResponse, WalletRequest>({
        query: (request: WalletRequest): FetchArgs => ({
          url: `/v1/beneficiaries/${request.beneficiaryId}/wallets`,
          params: { chain: request.chain },
        }),
      }),
      getBitsoBanksByCountryCode: build.query<readonly BitsoBank[], string>({
        query: (countryCode: string): string => `/v1/bitso/banks/${countryCode}`,
      }),
      getSpeiBanksByName: build.query<SpeiBank[], string>({
        query: (bankName: string): FetchArgs => ({
          url: `/v1/bank/spei/search`,
          params: {
            bankName,
          },
        }),
      }),
      getContacts: build.query<readonly Contact[], string | null>({
        query: (beneficiaryId: string | null): FetchArgs => ({
          url: beneficiaryId ? `/v1/contacts?beneficiaryId=${beneficiaryId}` : '',
        }),
        providesTags: ['Contacts'],
      }),
      getCustomer: build.query<Customer, void>({
        query: (): string => '/v1/customer-signup',
        providesTags: ['UserProfile'],
      }),
      getCustomerKYXVerifications: build.query<
        Page<CustomerKYXVerification>,
        GetCustomerKYXVerificationsFilters
      >({
        query: (params: GetCustomerKYXVerificationsFilters): FetchArgs => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return { url: '' };
          }

          const {
            type,
            name,
            verificationStatus,
            verificationResult,
            fromDate,
            toDate,
            page = 1,
            size = 10,
            sort = ['createdDate', 'desc'],
          } = params;

          return {
            url: '/v1/dashboard-kyx-verifications',
            params: {
              type: type,
              name: name,
              verificationStatus: verificationStatus,
              verificationResult: verificationResult,
              fromDate: fromDate,
              toDate: toDate,
              page: page - 1,
              size: size,
              sort: sort,
            },
          };
        },
        transformResponse: async (
          page: Page<CustomerKYXVerification> | undefined,
          _meta: unknown,
          params: GetCustomerKYXVerificationsFilters,
        ): Promise<Page<CustomerKYXVerification>> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const { page: pageNumber = 1, size = 10 } = params;

            return createMockCustomerKYXVerificationsPage(pageNumber, size);
          }

          if (!page) {
            throw new Error('Customer KYX verifications data not found');
          }

          return page;
        },
        providesTags: (
          result: Page<CustomerKYXVerification> | undefined,
        ): readonly TagDescription<TagTypes>[] =>
          result
            ? [
                { type: 'CustomerKYXVerifications', id: 'LIST' },
                ...result.content.map(
                  ({ id }: CustomerKYXVerification): TagDescription<TagTypes> =>
                    ({ type: 'CustomerKYXVerifications', id }) as const,
                ),
              ]
            : [{ type: 'CustomerKYXVerifications', id: 'LIST' }],
      }),
      getCustomerKYXVerification: build.query<CustomerKYXVerification, string>({
        query: (id: string): FetchArgs => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return { url: '' };
          }

          return {
            url: `/v1/dashboard-kyx-verifications/${id}`,
          };
        },
        transformResponse: async (
          customer: CustomerKYXVerification | undefined,
          _meta: unknown,
          id: string,
        ): Promise<CustomerKYXVerification> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const mockCustomer = getMockCustomerKYXVerification(id);

            if (!mockCustomer) {
              throw new Error(`Customer with ID ${id} not found`);
            }
            return mockCustomer;
          }

          if (!customer) {
            throw new Error('Customer KYX verification data not found');
          }

          return customer;
        },
        providesTags: (
          _result: CustomerKYXVerification | undefined,
          _error: FetchBaseQueryError | undefined,
          id: string,
        ): readonly TagDescription<TagTypes>[] => [{ type: 'CustomerKYXVerifications', id }],
      }),
      createCustomerKYXVerification: build.mutation<
        DashboardKYXVerificationResponse,
        CreateDashboardKYXVerificationRequest
      >({
        query: (body: CreateDashboardKYXVerificationRequest): FetchArgs => ({
          url: '/v1/dashboard-kyx-verifications',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'CustomerKYXVerifications', id: 'LIST' }],
      }),
      getConfiguration: build.query<ConfigurationResponse, void>({
        query: (): string => '/v1/configuration',
        providesTags: ['Configuration'],
        keepUnusedDataFor: 60 * 60 * 24, // 24 hours,
      }),
      getDashboardOperations: build.query<
        readonly DashboardOperation[],
        'DEPOSIT' | 'TRANSFER' | 'WITHDRAWAL' | 'EXCHANGE'
      >({
        query: (operation: 'DEPOSIT' | 'TRANSFER' | 'WITHDRAWAL' | 'EXCHANGE'): string =>
          `/v1/flow-of-funds/dashboard/operations/${operation}`,
        providesTags: ['Currencies'],
      }),
      getDetailedMultiCurrencyBalances: build.query<
        readonly DetailedBalance[],
        DetailedMultiCurrenciesBalancesRequest
      >({
        query: (request: DetailedMultiCurrenciesBalancesRequest): FetchArgs => ({
          url: `/v1/beneficiaries/${request.beneficiaryId}/multi-currencies-balances/details`,
          params: {
            currency: request.currency,
          },
        }),
        providesTags: ['Balances'],
        keepUnusedDataFor: 0,
      }),
      getDetailedBalance: build.query<DetailedBalance, DetailedBalancesRequest>({
        query: (request: DetailedBalancesRequest): FetchArgs => ({
          url: `/v1/beneficiaries/${request.beneficiaryId}/balances/details`,
          params: {
            currency: request.currency,
          },
        }),
        providesTags: getBalancesTagDescription,
        keepUnusedDataFor: 0,
      }),
      getFiatAccount: build.query<FiatAccount, string>({
        query: (id: string | null): string => `/v1/fiat-accounts/${id}`,
        providesTags: (result: FiatAccount | undefined): readonly TagDescription<TagTypes>[] => [
          { type: 'FiatAccounts', id: result?.id },
        ],
      }),
      getFiatAccounts: build.query<
        Page<FiatAccount>,
        { beneficiaryId: string | null; size?: number }
      >({
        query: ({
          beneficiaryId,
          size,
        }: {
          beneficiaryId: string | null;
          size?: number;
        }): string => {
          if (!beneficiaryId) {
            return '';
          }
          const params = size ? `?size=${size}` : '';
          return `/v1/fiat-accounts/beneficiaries/${beneficiaryId}${params}`;
        },
        providesTags: ['FiatAccounts'],
      }),
      getSimulation: build.query<SimulationResponse, string | null>({
        query: (simulationId: string | null): FetchArgs => ({
          url: `/v1/simulations/${simulationId}`,
        }),
        providesTags: ['Simulation'],
      }),
      getTransaction: build.query<TransactionResponse, string>({
        query: (transactionId: string): string => `/v1/transactions/${transactionId}`,
      }),
      downloadTransactionDetails: build.query<void, DownloadTransactionDocumentRequest>({
        query: (request: DownloadTransactionDocumentRequest): FetchArgs => ({
          url: `/v1/transactions/${request.transactionId}/proof-of-payment`,
          params: { type: request.type ?? ProofOfPaymentType.PAYMENT_CONFIRMATION },
          responseHandler: async (response: Response): Promise<void> => {
            if (response.status > 199 && response.status < 300) {
              const blob = await response.blob();
              const contentDisposition = response.headers.get('content-disposition');
              const filename = extractFileNameFromContentDispositionHeader(contentDisposition);

              const link = document.createElement('a');

              link.href = URL.createObjectURL(blob);
              link.download = filename ?? 'transaction-details.pdf';
              link.click();

              URL.revokeObjectURL(link.href);
            }
          },
        }),
      }),
      getTransactionDetails: build.query<TransactionWithDocuments, string>({
        query: (transactionId: string): string => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return '';
          }
          return `/v1/transactions/${transactionId}/:withDocuments`;
        },
        transformResponse: async (
          transaction: TransactionWithDocuments | undefined,
          _meta: unknown,
          transactionId: string,
        ): Promise<TransactionWithDocuments> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const mockTransaction = createMockTransactionDetails(transactionId);
            if (!mockTransaction) {
              throw new Error(`Transaction ${transactionId} not found`);
            }
            return mockTransaction;
          }

          if (!transaction) {
            throw new Error('Transaction data not found');
          }

          return {
            ...transaction,
            fromAccount: resolveFromAccount(transaction),
            toAccount: resolveToAccount(transaction),
            toRecipient: transaction.toRecipient ?? resolveToRecipient(transaction),
            documents: transaction.documents || [],
          };
        },
        providesTags: ['Transactions'],
      }),
      getTransactionStatus: build.query<readonly TransactionNotification[], string | null>({
        query: (transactionId: string | null): FetchArgs => ({
          url: `/v1/transactions/${transactionId}/notifications`,
        }),
      }),
      getTransactionStatusLogs: build.query<TransactionStatusLogResponse, string>({
        query: (transactionId: string): FetchArgs => {
          // Return empty URL for mock data in development mode
          if (shouldMock()) {
            return { url: '' };
          }
          return { url: `/v1/transactions/${transactionId}/status-logs` };
        },
        transformResponse: async (
          statusLogs: TransactionStatusLogResponse | undefined,
          _meta: unknown,
          transactionId: string,
        ): Promise<TransactionStatusLogResponse> => {
          // Return mock data in development mode
          if (shouldMock()) {
            return getMockTransactionStatusLogs(transactionId);
          }

          if (!statusLogs) {
            throw new Error('Status logs data not found');
          }

          return statusLogs;
        },
      }),
      getTransactions: build.query<Page<Transaction>, Partial<GetTransactionsFilters>>({
        providesTags: ['Transactions'],
        query: (filters: Partial<GetTransactionsFilters>): FetchArgs => {
          // Return mock data in development mode
          if (shouldMock()) {
            return { url: '' }; // Empty URL will be handled by customBaseQuery to return undefined
          }

          const {
            id,
            amount,
            type,
            sort,
            status,
            fromDate,
            toDate,
            page = 1,
            size,
            currency,
            recipientId,
            beneficiaryId,
            onlyWithIntegratorSpread,
          } = filters;

          return {
            url: `/v1/transactions/`,
            params: {
              id: id,
              dashboard: true,
              amount: amount,
              page: page - 1,
              size: size,
              type: type === TransactionType.ALL ? undefined : type,
              status: status === SimplifiedTransactionStatus.ALL ? undefined : status,
              fromDate: fromDate ? fromDate : undefined,
              toDate: toDate ? toDate : undefined,
              currency: currency === CurrencyCodeType.ANY ? undefined : currency,
              recipientId: recipientId ? recipientId : undefined,
              beneficiaryId: beneficiaryId ? beneficiaryId : undefined,
              onlyWithIntegratorSpread: onlyWithIntegratorSpread === true ? true : undefined,
              sort: sort,
            },
          };
        },
        transformResponse: async (
          page: Page<Transaction>,
          _meta: unknown,
          filters: Partial<GetTransactionsFilters>,
        ): Promise<Page<Transaction>> => {
          // Return mock data in development mode
          if (shouldMock()) {
            const { page: pageNum = 0, size = 20 } = filters;
            return createMockTransactionsPage(pageNum, size);
          }

          const { content } = page;

          return {
            ...page,
            content: content.map((transaction: Transaction): Transaction => {
              return {
                ...transaction,
                fromAccount: resolveFromAccount(transaction),
                toAccount: resolveToAccount(transaction),
                toRecipient: transaction.toRecipient ?? resolveToRecipient(transaction),
              };
            }),
          };
        },
      }),
      getTransactionsCSV: build.query<
        {
          readonly data: string;
          readonly filename: string;
        },
        Partial<GetTransactionsFilters>
      >({
        query: (filters: Partial<GetTransactionsFilters>): FetchArgs => {
          const {
            type,
            status,
            fromDate,
            toDate,
            recipientId,
            beneficiaryId,
            currency,
            sort,
            page = 1,
            size,
          } = filters;

          return {
            url: `/v1/transactions`,
            responseHandler: (response: Response): Promise<string> => response.text(),
            params: {
              dashboard: true,
              csv: true,
              page: page - 1,
              size: size,
              type: type === TransactionType.ALL ? undefined : type,
              status: status === SimplifiedTransactionStatus.ALL ? undefined : status,
              fromDate: fromDate ? fromDate : undefined,
              toDate: toDate ? toDate : undefined,
              currency: currency === CurrencyCodeType.ANY ? undefined : currency,
              recipientId: recipientId ? recipientId : undefined,
              beneficiaryId: beneficiaryId ? beneficiaryId : undefined,
              sort: sort,
            },
          };
        },
        transformResponse: async (
          data: string,
          meta?: BaseQueryMeta<BaseQuery>,
        ): Promise<{
          readonly filename: string;
          readonly data: string;
        }> => {
          const contentDisposition = meta?.response?.headers?.get('content-disposition') ?? '';
          const filename =
            extractFileNameFromContentDispositionHeader(contentDisposition) || 'transactions.csv';

          return {
            filename: filename,
            data: data,
          };
        },
        providesTags: ['Transactions'],
      }),
      getVirtualAccount: build.query<PaymentInstructionsResponse, PaymentInstructionsRequest>({
        query: (request: PaymentInstructionsRequest): string => {
          if (shouldMock()) {
            return '';
          }
          return `/v1/payments/payment-instructions?beneficiaryId=${request.beneficiaryId}&paymentRail=${request.paymentRail}`;
        },
        transformResponse: (
          response: PaymentInstructionsResponse | undefined,
          _meta: unknown,
          request: PaymentInstructionsRequest,
        ): PaymentInstructionsResponse => {
          if (shouldMock()) {
            return (
              mockPaymentInstructions[request.paymentRail] ||
              mockPaymentInstructions[PaymentRailType.ACH]
            );
          }
          if (!response) {
            throw new Error('No payment instructions found');
          }
          return response;
        },
      }),
      updateContact: build.mutation<Contact, UpdateContactRequest>({
        query: (request: UpdateContactRequest): FetchArgs => ({
          url: `/v1/contacts/${request.id}`,
          method: 'PUT',
          body: request.data,
        }),
        invalidatesTags: ['Contacts'],
      }),
      updateDepositTransactionHash: build.mutation<
        TransactionResponse,
        UpdateDepositTransactionHashRequest
      >({
        query: (request: UpdateDepositTransactionHashRequest): FetchArgs => ({
          url: `/v1/transactions/${request.transactionId}/depositTransactionHash`,
          method: 'PUT',
          body: {
            transactionHash: request.transactionHash,
          },
        }),
      }),
      updateFiatAccount: build.mutation<CreateFiatAccountResponse, UpdateFiatAccountRequest>({
        query: (request: UpdateFiatAccountRequest): FetchArgs => ({
          url: `/v1/fiat-accounts/${request.id}`,
          method: 'PUT',
          body: request.data,
        }),
        invalidatesTags: ['FiatAccounts', 'Contacts'],
      }),
      getUserPreferences: build.query<UserPreferencesResponse, void>({
        query: (): string => '/v1/preferences',
        providesTags: ['UserPreferences'],
      }),
      resendInvitation: build.mutation<unknown, string>({
        query: (userId: string): FetchArgs => ({
          url: `/v1/users/${userId}/invite`,
          method: 'POST',
        }),
      }),
      createUsers: build.mutation<User, CreateUserRequest>({
        query: (request: CreateUserRequest): FetchArgs => ({
          url: '/v1/users',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Users'],
      }),
      updateUsers: build.mutation<User, UpdateUserRequest>({
        query: (request: UpdateUserRequest): FetchArgs => {
          const { id, ...body } = request;

          return {
            url: `/v1/users/${id}`,
            method: 'PUT',
            body: body,
          };
        },
        invalidatesTags: ['Users'],
      }),
      deleteUsers: build.mutation<void, string>({
        query: (userId: string): FetchArgs => ({
          url: `/v1/users/${userId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Users'],
      }),
      getUserByEmail: build.query<User, string>({
        query: (email: string): FetchArgs => ({
          url: `/v1/users/?email=${email}`,
        }),
        providesTags: ['Users'],
      }),
      getUsers: build.query<Page<User>, Partial<GetUsersFilters> | void>({
        query: (filters: Partial<GetUsersFilters> = {}): FetchArgs => {
          const { page = 1, size = 15, search, role, status, department } = filters;

          return {
            url: '/v1/users',
            params: {
              page: page - 1,
              size: size,
              search: search,
              role: role,
              status: status,
              department: department,
            },
          };
        },
        providesTags: ['Users'],
      }),
      updateUserPreferences: build.mutation<UserPreferencesResponse, UpdateUserPreferencesRequest>({
        query: (request: UpdateUserPreferencesRequest): FetchArgs => ({
          url: '/v1/preferences',
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: ['UserPreferences'],
      }),
      updateThemePreferences: build.mutation<void, { theme: string }>({
        query: (request: { theme: string }): FetchArgs => ({
          url: '/v1/preferences/theme',
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: ['UserPreferences'],
      }),
      createTotpCredential: build.mutation<CreateTotpResponse, CreateTotpRequest>({
        query: (request: CreateTotpRequest): FetchArgs => ({
          url: '/v1/preferences/totp',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['UserPreferences'],
      }),
      uploadFile: build.mutation<unknown, UploadFileRequest>({
        query: ({ path, file }: UploadFileRequest): FetchArgs => {
          const body = new FormData();
          body.append('file', file);

          return {
            url: `/v1/customer-signup/${path}`,
            method: 'POST',
            body: body,
          };
        },
        onQueryStarted: async (
          request: UploadFileRequest,
          // React toolkit is a stupid toolkit that thinks
          // it knows better. And that type inference is better
          // than explicitly stating what thing is what type.
          //
          // IMHO this makes having types completely pointless.
          //
          // eslint-disable-next-line @typescript-eslint/typedef
          { dispatch, queryFulfilled },
        ): Promise<void> => {
          if (isCustomerFilePath(request.path)) {
            const { util } = calizaApi;

            const patchResult = dispatch(
              util.updateQueryData('getCustomer', undefined, customerFileUploadPatcher(request)),
            );

            try {
              await queryFulfilled;
            } catch {
              // TODO: Dispatch an action to indicate there was an error and perhaps
              //       give feedback to the user.
              patchResult.undo();
            }
          }
        },
      }),
      getGroups: build.query<readonly Group[], void>({
        query: (): FetchArgs => ({
          url: '/v1/groups',
        }),
        providesTags: ['Groups'],
      }),
      createGroup: build.mutation<Group, CreateGroupRequest>({
        query: (request: CreateGroupRequest): FetchArgs => ({
          url: '/v1/groups',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Groups'],
      }),
      updateGroup: build.mutation<Group, UpdateGroupRequest>({
        query: (request: UpdateGroupRequest): FetchArgs => {
          const { groupId, ...body } = request;
          return {
            url: `/v1/groups/${groupId}`,
            method: 'PUT',
            body: body,
          };
        },
        invalidatesTags: ['Groups'],
      }),
      getBeneficiaries: build.query<readonly Beneficiary[], void>({
        query: (): FetchArgs => ({
          url: '/v1/beneficiaries?withoutPagination=true',
        }),
        providesTags: ['Beneficiaries'],
        transformResponse: (response: BeneficiariesPageResponse): readonly Beneficiary[] => {
          return response.content.map((apiResponse: BeneficiaryApiResponse): Beneficiary => {
            const name =
              apiResponse.type === BeneficiaryType.business
                ? (apiResponse.business?.name ?? '')
                : `${apiResponse.person?.firstName ?? ''} ${apiResponse.person?.lastName ?? ''}`.trim();

            return {
              id: apiResponse.id,
              name: name,
            };
          });
        },
      }),
      createPoboPayoutTransaction: build.mutation<PoboPayoutResponse, PoboPayoutRequest>({
        query: (request: PoboPayoutRequest): FetchArgs => ({
          url: '/v1/transactions/pobo-payouts',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: (
          result: PoboPayoutResponse | undefined,
        ): readonly TagDescription<TagTypes>[] => {
          if (!result) {
            return [];
          }

          return ['Balances', 'Transactions'];
        },
      }),
      getBeneficiaryVirtualAccounts: build.query<VirtualAccount[], string>({
        async queryFn(
          beneficiaryId: string,
          _queryApi: BaseQueryApi,
          _extraOptions: object,
          fetchWithBQ: (url: string) => ReturnType<BaseQuery>,
        ): Promise<
          QueryReturnValue<VirtualAccount[], FetchBaseQueryError, FetchBaseQueryMeta | undefined>
        > {
          const result = await fetchWithBQ(`/v1/beneficiaries/${beneficiaryId}/virtualAccounts`);

          // Handle 404: "no virtual accounts exist yet" → return empty array
          if (result.error) {
            const error = result.error as FetchBaseQueryError;
            if (error.status === 404) {
              return { data: [] };
            }
            // Re-throw other errors (500, network errors, etc.)
            return { error };
          }

          return { data: result.data as VirtualAccount[] };
        },
        providesTags: ['BeneficiaryVirtualAccounts'],
      }),
      getPendingAnnouncements: build.query<PendingAnnouncementsResponse, void>({
        query: (): FetchArgs => {
          if (shouldMock()) {
            return { url: '' };
          }
          return { url: '/v1/announcements/pending' };
        },
        transformResponse: (
          response: PendingAnnouncementsResponse | undefined,
        ): PendingAnnouncementsResponse => {
          if (shouldMock()) {
            return mockPendingAnnouncements;
          }
          return response ?? { announcements: [] };
        },
        providesTags: ['Announcements'],
      }),
      acknowledgeAnnouncement: build.mutation<void, string>({
        query: (id: string): FetchArgs => ({
          url: `/v1/announcements/${id}/acknowledge`,
          method: 'POST',
        }),
        invalidatesTags: ['Announcements'],
      }),
      createBeneficiaryVirtualAccount: build.mutation<void, CreateBeneficiaryVirtualAccountRequest>(
        {
          query: (request: CreateBeneficiaryVirtualAccountRequest): FetchArgs => {
            const { beneficiaryId, ...body } = request;

            return {
              url: `/v1/beneficiaries/${beneficiaryId}/virtualAccounts`,
              method: 'POST',
              body: body,
              // Handle 204 No Content response - don't try to parse as JSON
              responseHandler: 'text',
            };
          },
          invalidatesTags: ['BeneficiaryVirtualAccounts'],
        },
      ),
      downloadAccountDetailsPdf: build.query<
        void,
        { beneficiaryId: string; virtualAccountId: string }
      >({
        query: ({
          beneficiaryId,
          virtualAccountId,
        }: {
          beneficiaryId: string;
          virtualAccountId: string;
        }): FetchArgs => ({
          url: `/v1/beneficiaries/${beneficiaryId}/virtual-accounts/${virtualAccountId}/account-details-pdf`,
          responseHandler: async (response: Response): Promise<void> => {
            if (response.status > 199 && response.status < 300) {
              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
              URL.revokeObjectURL(url);
            }
          },
        }),
      }),
      getCountryStates: build.query<readonly NamedIdentified[], string>({
        async queryFn(
          country: string,
        ): Promise<
          QueryReturnValue<
            readonly NamedIdentified[],
            FetchBaseQueryError,
            FetchBaseQueryMeta | undefined
          >
        > {
          try {
            const response = await fetch(`/countries/${country.toLowerCase()}/index.json`);

            if (!response.ok) {
              return { data: [] };
            }

            const resolved: unknown = await response.json();

            if (
              resolved &&
              typeof resolved === 'object' &&
              'states' in resolved &&
              isArrayOf(resolved.states, isNamedIdentified)
            ) {
              return { data: resolved.states };
            }

            return { data: [] };
          } catch (error) {
            return {
              error: {
                status: 'FETCH_ERROR',
                error: error instanceof Error ? error.message : 'Failed to fetch country states',
              },
            };
          }
        },
      }),
    };
  },
  reducerPath: reducerPath,
  tagTypes: [
    'UserProfile',
    'UserPreferences',
    'Currencies',
    'Simulation',
    'Contacts',
    'Balances',
    'Transactions',
    'FiatAccounts',
    'Recipients',
    'Users',
    'Groups',
    'Configuration',
    'CustomerKYXVerifications',
    'Beneficiaries',
    'BeneficiaryVirtualAccounts',
    'IntegratorTotalEarnings',
    'IntegratorRevenueSummary',
    'Announcements',
  ],
});

export const {
  useGetBeneficiaryWalletQuery,
  useGetTransactionQuery,
  useGetVirtualAccountQuery,
  useUpdateDepositTransactionHashMutation,
  useGetCustomerQuery,
  useGetCustomerKYXVerificationsQuery,
  useGetCustomerKYXVerificationQuery,
  useCreateCustomerKYXVerificationMutation,
  useGetConfigurationQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useCreateTotpCredentialMutation,
  useUploadFileMutation,
  useDeleteFileMutation,
  useDeleteFiatAccountMutation,
  useCreateRecipientMutation,
  useDeleteRecipientMutation,
  useGetRecipientQuery,
  useGetRecipientsQuery,
  useUpdateRecipientNicknameMutation,
  useDownloadFileInfoQuery,
  useDownloadFileQuery,
  useGetDashboardOperationsQuery,
  useCreateSimulationMutation,
  useCreateIntegratorMutation,
  useCreateTransactionMutation,
  useGetTransactionStatusQuery,
  useGetTransactionStatusLogsQuery,
  useCreateFakePIXPaymentMutation,
  useCreateFakeSpeiPaymentMutation,
  useGetSimulationQuery,
  useGetContactsQuery,
  useDeleteContactMutation,
  useCreateContactMutation,
  useCreateFiatAccountMutation,
  useBankLookupQuery,
  useGetSpeiBanksByNameQuery,
  useSwiftCodeLookupQuery,
  useLazyIbanCodeLookupQuery,
  useGetDetailedBalanceQuery,
  useGetDetailedMultiCurrencyBalancesQuery,
  useUpdateFiatAccountMutation,
  useGetFiatAccountQuery,
  useUpdateContactMutation,
  useGetFiatAccountsQuery,
  useGetTransactionsQuery,
  useGetTransactionDetailsQuery,
  useLazyGetTransactionsCSVQuery,
  useGetBitsoBanksByCountryCodeQuery,
  useLazyDownloadTransactionDetailsQuery,
  useLazyAutocompleteAddressQuery,
  useLazyGetPlaceDetailsQuery,
  useGetUsersQuery,
  useCreateUsersMutation,
  useUpdateUsersMutation,
  useDeleteUsersMutation,
  useResendInvitationMutation,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useGetUserByEmailQuery,
  useGetBeneficiariesQuery,
  useCreatePoboPayoutTransactionMutation,
  useGetOwnIntegratorFeaturesQuery,
  useGetIntegratorTotalEarningsQuery,
  useGetIntegratorRevenueSummaryQuery,
  useWithdrawIntegratorEarningsMutation,
  useUploadEfxTransactionACAM220fileMutation,
  useGetBeneficiaryVirtualAccountsQuery,
  useCreateBeneficiaryVirtualAccountMutation,
  useLazyDownloadAccountDetailsPdfQuery,
  useGetPendingAnnouncementsQuery,
  useAcknowledgeAnnouncementMutation,
  useGetCountryStatesQuery,
} = calizaApi;

// Gather all endpoints here that we need to export selectors for
const { getCustomer, getDashboardOperations, getUserPreferences, getTransactionStatusLogs } =
  calizaApi.endpoints;

// Export selectors for each endpoint
export const transactionFlowsSelector = getDashboardOperations.select;
export const customerSelector = getCustomer.select();
export const preferencesSelector = getUserPreferences.select();
export const statusLogsSelector = getTransactionStatusLogs.select;
